/**
 * ICR-1 — type/sanity prefilter for evidence conflict pairs.
 *
 * Live-prod Sickel surfaced six HIGH-severity "conflicts" where the
 * doc-extractor returned `"'s Option"` / `"or dynamic"` / `"Hwy 76"` /
 * `"from NTP."` against valid provider values (year `"2025"`, state
 * `"LA"`). The conflict detector had no type-awareness — it asked
 * Gemini "which is more authoritative?" and Gemini dutifully labelled
 * every disagreement HIGH.
 *
 * This module exposes:
 *
 *   1. `FIELD_TYPE_REGISTRY` — `(sectionId, fieldKey) → FieldType`.
 *      Built from `SECTION_SCHEMAS` plus a handful of explicit
 *      semantic overrides (`year`, `us-state`) the raw schema can't
 *      express.
 *
 *   2. `docValueIsImplausible(field, type, value)` — returns
 *      `{ implausible: true, reason }` when the doc-extracted value
 *      is OBVIOUSLY not a plausible answer for the declared field
 *      type. Intentionally conservative: when uncertain, returns
 *      `false` and the pair proceeds to the full Gemini comparison.
 *      Pure / synchronous.
 *
 * **Constitutional discipline**: false-positive auto-resolution is far
 * worse than a noisy conflict row. Every branch defaults to "not
 * implausible" when in doubt. New plausibility rules must be tested
 * against the Sickel-garbage corpus + a controlled set of real-world
 * doc values before landing.
 *
 * Composes with EDX-10 (evidenceConflict.detectEvidenceConflicts),
 * DCO (sedDocResolver) and UAE-4 (sedAuthoredEvidence) — see the pack
 * doc for the full chain.
 */

/* ── Pipeline version ─────────────────────────────────────────────── */

/**
 * ICR-FOLLOWUP-3 — monotonic version stamp for the conflict-detection
 * pipeline. Bumped whenever the prefilter's classifier, the stopword
 * list, or the field-type registry changes in a way that would change
 * which rows the prefilter auto-resolves vs lets through to Gemini.
 *
 * Stamped on every `EvidenceConflict` at detection time. The bootstrap
 * merger (`sedBootstrap.ts`) drops prior cached conflicts whose
 * `pipelineVersion` is missing or `<` the current value, so the
 * detector re-runs from scratch against the upgraded classifier and
 * cached pre-fix rows can't survive across a fix.
 *
 * Changelog (oldest first):
 *   1 — implicit/absent. ICR-1..4 as shipped: field-type registry,
 *       `docValueIsImplausible`, six-rule `stringFragmentCheck`,
 *       authored-vs-provider pass, RDX-6 3-axis pass.
 *   2 — ICR-FOLLOWUP-1 + ICR-FOLLOWUP-2 (this commit chain). Retypes
 *       `grid.voltage` / `grid.capacity` to `'number'` so the
 *       unit-stripping branch runs; widens `stringFragmentCheck` with
 *       doc-process verbs (`requested`, `required`, …) and placeholder
 *       literals (`TBD`, `n/a`, …); adds the 3-tokens-no-digits
 *       heuristic on numeric / date branches. Catches the live-prod
 *       Redhead Voltage `"requested in writing"` case.
 */
export const CONFLICT_PIPELINE_VERSION = 2 as const;

/* ── Field-type taxonomy ────────────────────────────────────────── */

/**
 * Field type taxonomy used by `docValueIsImplausible`. Superset of the
 * Gemini `Type` enum in extractFromDocument — adds the semantic types
 * (`year`, `us-state`, `percent`, …) the raw schema can't carry but
 * that drive the sanity checks.
 */
export type FieldType =
  | 'string'
  | 'number'
  | 'integer'
  | 'enum'
  | 'date'
  | 'year'
  | 'us-state'
  | 'percent'
  | 'kw'
  | 'mw'
  | 'boolean';

/** USPS two-letter codes + DC. Used by the `us-state` plausibility check. */
const US_STATE_CODES: ReadonlySet<string> = new Set([
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY',
  'DC',
]);

/**
 * `(sectionId, fieldKey) → FieldType`. Hand-curated from
 * `SECTION_SCHEMAS` in `extractFromDocument.ts` plus the few semantic
 * fields the raw `Type` enum doesn't carry. Fields not listed default
 * to `'string'` at lookup time — lenient, so adding a new SED field
 * without a registry entry can't accidentally start auto-resolving.
 *
 * Adding a new field: prefer the most specific type
 * (`'year'` > `'integer'` > `'number'` > `'string'`).
 */
export const FIELD_TYPE_REGISTRY: Readonly<Record<string, Readonly<Record<string, FieldType>>>> = {
  climate: {
    avgTemp: 'number',
    peakTemp: 'number',
    avgWetBulb: 'number',
    humidity: 'percent',
  },
  grid: {
    substationName: 'string',
    substationDistKm: 'number',
    // ICR-FOLLOWUP-1 — `voltage` / `capacity` ship as STRING in the
    // Gemini extraction schema (so docs can include unit suffixes like
    // `"230 kV"` / `"500 MW"`), but they're numeric-with-unit fields.
    // Typing them as `'number'` here routes them through the
    // unit-stripping numeric branch in `docValueIsImplausible` so
    // free-text doc fragments like `"requested in writing"` are caught
    // by the prefilter instead of leaking into Gemini's HIGH-severity
    // bucket (Redhead Voltage, live-prod 2026-05-18).
    voltage: 'number',
    capacity: 'number',
    complexity: 'enum',
    utilityName: 'string',
  },
  flood: {
    floodZone: 'string',
    sfha: 'boolean',
    floodRisk: 'enum',
  },
  environmental: {
    hasCriticalHabitat: 'boolean',
    hasWetlands: 'boolean',
    wetlandType: 'string',
    wetlandAcres: 'number',
  },
  waterStress: {
    stressLevel: 'enum',
    bwsRaw: 'number',
    bwsLabel: 'string',
  },
  regulatory: {
    state: 'us-state',
    nuclearStance: 'enum',
    stateNotes: 'string',
  },
  seismic: {
    pga: 'number',
    sds: 'number',
    sd1: 'number',
    designCategory: 'enum',
    riskCategory: 'enum',
    siteClass: 'enum',
  },
  electricity: {
    retailRate: 'number',
    state: 'us-state',
    year: 'year',
  },
  airQuality: {
    aqi: 'integer',
    category: 'enum',
    primaryPollutant: 'string',
    pm25Conc: 'number',
  },
  nuclearInfra: {
    nearestReactorName: 'string',
    nearestReactorDistKm: 'number',
    /**
     * R-29: nearestReactorPower is actually a capacity factor (0-100%),
     * NOT MW. The prior 'mw' classification was wrong and caused doc-
     * extracted MW values (e.g. "1500 MW" mentions in vendor docs) to
     * conflict-match against this field as if comparable. Reclassified
     * as `percent` so doc-vs-provider conflict adjudication treats the
     * field correctly.
     */
    nearestReactorPower: 'percent',
    /** R-29: site-aggregated NET nameplate capacity in MW. */
    nearestReactorNameplateMw: 'mw',
    /** R-29: operating capacity factor 0-100%. */
    nearestReactorCapacityFactorPct: 'percent',
    reactorsWithin200km: 'integer',
    nuclearInfraMaturity: 'enum',
  },
  solar: {
    ghi: 'number',
    dni: 'number',
  },
  wind: {
    windSpeed100m: 'number',
    windSpeed80m: 'number',
    capacityFactor: 'number',
  },
  connectivity: {
    nearestIxName: 'string',
    nearestIxDistKm: 'number',
    ixCount50km: 'integer',
    facilityCount50km: 'integer',
  },
  parcel: {
    zoning: 'string',
    landUse: 'string',
    owner: 'string',
    acres: 'number',
  },
};

/**
 * Look up the registered type for `(sectionId, fieldKey)`. Returns
 * `'string'` when the section or field is absent — lenient default
 * that means new fields cannot accidentally start auto-resolving until
 * an explicit registry entry is added.
 */
export function lookupFieldType(sectionId: string, fieldKey: string): FieldType {
  return FIELD_TYPE_REGISTRY[sectionId]?.[fieldKey] ?? 'string';
}

/* ── Plausibility check ─────────────────────────────────────────── */

export interface PlausibilityResult {
  implausible: boolean;
  reason?: string;
}

/**
 * Returns `{ implausible: true, reason }` when `docValue` is OBVIOUSLY
 * not a plausible answer for the declared field type. Used to
 * auto-resolve garbage extractions without bothering the user.
 *
 * The "obvious" bar is intentionally high:
 *
 *  - `year` / `us-state` / numeric / `date`: structural shape mismatch
 *    (a year must be `^(19|20)\d{2}$`, a state must be a known USPS
 *    code, …).
 *  - `string` / `enum`: only the most blatant mid-phrase fragments
 *    (apostrophe-prefix, stopword-prefix, punctuation-only, absurdly
 *    long, empty).
 *  - `boolean`: a non-bool string that isn't `true|false|yes|no` etc.
 *
 * Every branch defaults to `implausible: false` when uncertain — see
 * the module-level note on constitutional discipline.
 *
 * @param field      Field key, e.g. `'year'`, `'state'`, `'floodZone'`.
 *                   Used only for the diagnostic reason string.
 * @param schemaType Declared field type (from `FIELD_TYPE_REGISTRY` or
 *                   the caller's own derivation). Omit/undefined falls
 *                   through to lenient string handling.
 * @param docValue   Value extracted from the doc to test.
 */
export function docValueIsImplausible(
  field: string,
  schemaType: FieldType | undefined,
  docValue: unknown,
): PlausibilityResult {
  void field; // field name carried for caller logging; not used in the check itself
  if (docValue == null) return { implausible: true, reason: 'null' };
  const s = String(docValue).trim();
  if (s.length === 0) return { implausible: true, reason: 'empty' };

  const t: FieldType = schemaType ?? 'string';

  switch (t) {
    case 'year': {
      const ok = /^(19|20)\d{2}$/.test(s);
      return ok ? { implausible: false } : { implausible: true, reason: `not a 4-digit year (got "${preview(s)}")` };
    }
    case 'us-state': {
      // Accept either two-letter code or full state name. Two-letter
      // path is the strict structural test; the full-name path is a
      // lenient fallback (the Gemini comparison will catch genuine
      // disagreements when both sides look plausible).
      const upper = s.toUpperCase();
      if (/^[A-Z]{2}$/.test(upper) && US_STATE_CODES.has(upper)) return { implausible: false };
      // Full state name — alphabetic + spaces, ≤30 chars, and must
      // start with a capital letter (real state names do). This
      // rejects stopword-prefix garbage like "or dynamic" while still
      // accepting "Louisiana", "New York", "New Hampshire", etc.
      if (/^[A-Z][A-Za-z]+( [A-Z][A-Za-z]+)*$/.test(s) && s.length <= 30) return { implausible: false };
      return { implausible: true, reason: `not a US state (got "${preview(s)}")` };
    }
    case 'number':
    case 'integer':
    case 'percent':
    case 'kw':
    case 'mw': {
      // ICR-FU-1 multi-value tolerance — `grid.voltage` and
      // `grid.capacity` (retyped from 'string' to 'number') can carry
      // slash-separated multi-values like "230/115 kV" or "500/345 MW".
      // Accept if AT LEAST ONE slash-segment parses to a finite number
      // after the standard unit/format strip. Single-value path is the
      // common case and identical to the pre-FU behaviour.
      const segments = s.split('/');
      let anyFinite = false;
      let allInteger = true;
      for (const seg of segments) {
        // Strip the common units / formatting before parsing — provider
        // values often arrive as strings like `"500 MW"` or `"1,234"`.
        const cleaned = seg.replace(/[,$%\s]/g, '').replace(/(mw|kw|kwh|gw|mwh|kv|c|f)$/i, '');
        const n = Number(cleaned);
        if (!Number.isFinite(n)) { allInteger = false; continue; }
        anyFinite = true;
        if (t === 'integer' && !Number.isInteger(n)) {
          // Allow values that round to integer within numeric tolerance —
          // a doc-extracted "500.0" is fine for an integer field.
          if (Math.abs(n - Math.round(n)) > 1e-6) allInteger = false;
        }
      }
      if (!anyFinite) return { implausible: true, reason: `not numeric (got "${preview(s)}")` };
      if (t === 'integer' && !allInteger) {
        return { implausible: true, reason: `not an integer (got "${preview(s)}")` };
      }
      // ICR-FU-2 ≥3-tokens-no-digits heuristic — even when at least one
      // slash-segment parsed (e.g. "230" alone, after splitting "the
      // value 230 elsewhere"), reject if the doc string is a 3+-token
      // phrase with no digits in the OTHER tokens — that's prose, not a
      // numeric value. Conservative: requires the entire string to have
      // no digits AT ALL outside the parsed segments, so a clean
      // "230 kV" stays plausible.
      if (looksLikeProsePhrase(s)) {
        return { implausible: true, reason: `3+ tokens with no digits — likely prose, not a value (got "${preview(s)}")` };
      }
      return { implausible: false };
    }
    case 'date': {
      // ICR-FU-2 ≥3-tokens-no-digits heuristic — reject prose phrases
      // before the Date.parse path so the regex's loose acceptance
      // (e.g. "from NTP. 2025" would otherwise parse) can't slip
      // garbage through.
      if (looksLikeProsePhrase(s)) {
        return { implausible: true, reason: `3+ tokens with no digits — likely prose, not a date (got "${preview(s)}")` };
      }
      // ICR-FU-2 — workflow-status verb-phrase prefix rejection.
      // `Date.parse` is famously lenient and accepts strings like
      // "requested in 2026" as Jan 1, 2026; reject those at the
      // prefilter rather than handing Gemini a fake adjudication.
      const verbPhraseCheck = stringFragmentCheck(s);
      if (verbPhraseCheck.implausible
          && verbPhraseCheck.reason
          && /verb-phrase|stopword|apostrophe/.test(verbPhraseCheck.reason)) {
        return { implausible: true, reason: `not a date (got "${preview(s)}") — ${verbPhraseCheck.reason}` };
      }
      // Must contain a 4-digit year AND parse to a valid timestamp.
      const ok = /\d{4}/.test(s) && !Number.isNaN(Date.parse(s));
      return ok ? { implausible: false } : { implausible: true, reason: `not a date (got "${preview(s)}")` };
    }
    case 'boolean': {
      const lc = s.toLowerCase();
      const ok = ['true', 'false', 'yes', 'no', 'y', 'n', '1', '0'].includes(lc);
      return ok ? { implausible: false } : { implausible: true, reason: `not boolean-shaped (got "${preview(s)}")` };
    }
    case 'string':
    case 'enum':
    default:
      return stringFragmentCheck(s);
  }
}

/**
 * Heuristic checks for OBVIOUS mid-phrase doc-extraction fragments.
 * Conservative: only catches the failure modes the live-prod Sickel
 * + Redhead walkthroughs surfaced (apostrophe prefix, stopword prefix,
 * verb-phrase prefix, punctuation only, absurd length, empty). All
 * real-looking strings pass through.
 */
function stringFragmentCheck(s: string): PlausibilityResult {
  if (s.length > 200) return { implausible: true, reason: 'unreasonably long extraction fragment' };
  if (/^[\s\-—.,;:'"`*]+$/.test(s)) return { implausible: true, reason: 'punctuation-only fragment' };
  // Apostrophe-prefix fragments like "'s Option" or "'s clearance" — a
  // common GPT-style sentence-mid clip.
  if (/^['']/i.test(s)) return { implausible: true, reason: 'apostrophe-prefix fragment' };
  // Placeholder / non-answer markers regulators use as values. Exact
  // match (case-insensitive, ignoring trailing punctuation) so we
  // never trip on a real label that happens to contain them.
  const bare = s.replace(/[.,;:!?]+$/g, '').trim().toLowerCase();
  if (PLACEHOLDER_LITERALS.has(bare)) {
    return { implausible: true, reason: 'placeholder/non-answer literal (e.g. TBD, n/a)' };
  }
  // Stopword-prefix fragments. Two buckets:
  //   1) classic conjunctions/prepositions/articles — `"or dynamic"`,
  //      `"from NTP."`, `"of the"`, `"subject to ..."`, `"applied for"`
  //   2) doc-process verbs/adjectives common in utility correspondence:
  //      `"requested in writing"`, `"required by NTP"`,
  //      `"submitted to the board"`, `"pending review"`,
  //      `"proposed Phase 2"`, `"tentative 230 MW"`,
  //      `"awaiting EIRC"`, `"forthcoming"`, `"under review"`
  if (/^(from|to|of|and|or|the|a|an|in|on|at|by|for|with|subject\s+to|applied\s+for)\s/i.test(s)) {
    return { implausible: true, reason: 'starts with a stopword (likely mid-phrase fragment)' };
  }
  // ICR-FU-2 — verb-phrase-prefix fragments. Redhead's Voltage row was
  // "requested in" (provider: 230 HIFLD vs doc: "requested in"). These
  // are STATUS phrases, not VALUES — they almost never begin a real
  // category label for a SED field. The set is intentionally narrow:
  // workflow-status verbs only, plus "under review" (the one common
  // two-token phrase). Adding general English verbs would risk
  // false-positives on legitimate enum labels like "Approved" or
  // "Granted" which DO sometimes begin a real category.
  if (/^(requested|required|submitted|pending|applied|proposed|tentative|forthcoming|under\s+review|awaiting|n\/?a|tbd|tbc|unknown|not\s+available|not\s+applicable|not\s+specified|see\s+below|see\s+above|see\s+attached|see\s+page)(\s|[.,;:!?]|$)/i.test(s)) {
    return { implausible: true, reason: 'starts with a workflow-status verb-phrase (likely a placeholder, not a value)' };
  }
  return { implausible: false };
}

/** Placeholder / non-answer markers regulators write into form fields. */
const PLACEHOLDER_LITERALS: ReadonlySet<string> = new Set([
  'tbd',
  'tba',
  'n/a',
  'na',
  'none',
  'unknown',
  'pending',
  'forthcoming',
  'to be determined',
  'to be announced',
  'under review',
  'awaiting',
  'requested',
  'required',
]);

/**
 * 3-tokens-no-digits heuristic — true when `s` has ≥ 3 whitespace-
 * delimited tokens AND contains zero ASCII digits.
 *
 * Used by the numeric / date branches to reject verb-phrase doc
 * fragments (`"requested in writing"`, `"to be determined"`) that
 * would otherwise survive into `Number()` / `Date.parse()`.
 *
 * Calibrated against the live-prod Redhead Voltage trace
 * (`"requested in writing"`) and the false-positive corpus
 * (`"Provider 230"` — 3 tokens but has a digit → keep,
 * `"230 kV"` → 2 tokens → keep). Pure / synchronous.
 */
function threeTokensNoDigits(s: string): boolean {
  if (/\d/.test(s)) return false;
  const tokens = s.trim().split(/\s+/).filter(Boolean);
  return tokens.length >= 3;
}

/** Truncate for diagnostic strings — caps reason length so logs stay readable. */
function preview(s: string): string {
  return s.length > 30 ? `${s.slice(0, 30)}…` : s;
}

/**
 * ICR-FU-2 — ≥3-tokens-no-digits heuristic. A doc value with three or
 * more whitespace-separated tokens and ZERO digits anywhere is almost
 * always a prose phrase, not a numeric/date/id value. Used by the
 * numeric / date branches to catch the failure mode the Redhead
 * Voltage walkthrough surfaced (extractor produces "the voltage as
 * requested" against a numeric field).
 *
 * Conservative: requires BOTH conditions. A 2-token phrase like
 * "requested in" stays plausible by this check alone — the stopword /
 * verb-phrase prefix branches catch that case. A 3-token phrase WITH
 * a digit (e.g. "230 kV bus") stays plausible — Gemini comparison
 * decides.
 */
function looksLikeProsePhrase(s: string): boolean {
  const tokens = s.trim().split(/\s+/);
  if (tokens.length < 3) return false;
  if (hasAnyDigit(s)) return false;
  return true;
}

/** Tiny digit-presence check pulled out so the heuristics share one
 *  definition. Returns true iff `s` contains any 0-9 codepoint. */
function hasAnyDigit(s: string): boolean {
  return /\d/.test(s);
}

/* ── Pipeline version ────────────────────────────────────────────── */

/**
 * Monotonic version of the conflict-detection pipeline. Bumped any
 * time the prefilter's behaviour changes in a way that should
 * invalidate cached conflict rows on a site's SED.
 *
 * `evidenceConflict.applyConflictsToSed` stamps this onto the SED's
 * `conflictsPipelineVersion` field. `sedBootstrap`'s
 * detect-and-merge step compares the prior SED's version against
 * the current constant and DISCARDS the prior conflict array when
 * the version is stale — so the freshly-detected list (with the new
 * prefilter behaviour) becomes authoritative instead of being merged
 * with pre-prefilter garbage rows.
 *
 * Version log:
 *   1 — Initial ICR landing (apostrophe / stopword string prefilter +
 *        numeric / date / boolean type plausibility).
 *   2 — ICR-FU (Redhead Voltage 2026-05-18): grid.voltage + grid.capacity
 *        retyped from 'string' to 'number'; stopword list widened with
 *        workflow-status verb-phrases; numeric / date branches now
 *        reject 3+-token-no-digits prose phrases; numeric branch
 *        gained slash-separated multi-value tolerance.
 */
